import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import API from "../services/api";
import { formatLocalDateTime } from "../utils/dateTime";
import { formatLiveValue } from "../utils/tagValue";
import "./HomePage.css";

const formatClock = (date) => (date ? formatLocalDateTime(date) : "—");

const HomePage = ({ theme = "light", onToggleTheme, access = null }) => {
    const [tags, setTags] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState("");
    const [lastSuccessAt, setLastSuccessAt] = useState(null);
    const [now, setNow] = useState(() => new Date());
    const [search, setSearch] = useState("");
    const [serverFilter, setServerFilter] = useState("all");
    const [groupFilter, setGroupFilter] = useState("all");
    const [connectionFilter, setConnectionFilter] = useState("all");
    const [sortKey, setSortKey] = useState("name");
    const [sortDir, setSortDir] = useState("asc");

    useEffect(() => {
        const clock = setInterval(() => setNow(new Date()), 1000);
        return () => clearInterval(clock);
    }, []);

    useEffect(() => {
        let active = true;

        const fetchTags = async (isInitial) => {
            if (isInitial) {
                setLoading(true);
            } else {
                setRefreshing(true);
            }

            try {
                const response = await API.get("/latest-tag-values/", { params: { context: "home" } });
                if (!active) {
                    return;
                }
                setTags(response.data);
                setLastSuccessAt(new Date());
                setError("");
            } catch (fetchError) {
                console.error("Error fetching tags:", fetchError);
                if (!active) {
                    return;
                }
                setError("Unable to refresh live tag data.");
            } finally {
                if (!active) {
                    return;
                }
                setLoading(false);
                setRefreshing(false);
            }
        };

        fetchTags(true);
        const interval = setInterval(() => fetchTags(false), 5000);
        return () => {
            active = false;
            clearInterval(interval);
        };
    }, []);

    const tagList = useMemo(() => (Array.isArray(tags) ? tags : []), [tags]);

    const metrics = useMemo(() => {
        const serverNames = new Set();
        let online = 0;
        let offline = 0;

        tagList.forEach((tag) => {
            if (tag?.server_name && tag.server_name !== "N/A") {
                serverNames.add(tag.server_name);
            }
            if (tag?.connection_status === "ONLINE") {
                online += 1;
            } else {
                offline += 1;
            }
        });

        return {
            total: tagList.length,
            servers: serverNames.size,
            online,
            offline,
        };
    }, [tagList]);

    const serverOptions = useMemo(() => {
        const names = new Set();
        tagList.forEach((tag) => {
            if (tag?.server_name) {
                names.add(tag.server_name);
            }
        });
        return Array.from(names).sort((left, right) =>
            String(left).localeCompare(String(right), undefined, { sensitivity: "base" })
        );
    }, [tagList]);

    const groupOptions = useMemo(() => {
        const names = new Set();
        tagList.forEach((tag) => {
            if (tag?.group_name) {
                names.add(tag.group_name);
            }
        });
        return Array.from(names).sort((left, right) =>
            String(left).localeCompare(String(right), undefined, { sensitivity: "base" })
        );
    }, [tagList]);

    const visibleTags = useMemo(() => {
        const query = search.trim().toLowerCase();

        const filtered = tagList.filter((tag) => {
            const name = String(tag?.tag_name ?? "").toLowerCase();
            const matchesSearch = query === "" || name.includes(query);
            const matchesServer = serverFilter === "all" || tag?.server_name === serverFilter;
            const matchesGroup = groupFilter === "all" || tag?.group_name === groupFilter;
            const connectionStatus = tag?.connection_status || "UNKNOWN";
            const matchesConnection = connectionFilter === "all"
                || connectionStatus === connectionFilter.toUpperCase();
            return matchesSearch && matchesServer && matchesGroup && matchesConnection;
        });

        return [...filtered].sort((left, right) => {
            if (sortKey === "time") {
                const leftMissing = !left?.timestamp || left.timestamp === "N/A";
                const rightMissing = !right?.timestamp || right.timestamp === "N/A";
                if (leftMissing && rightMissing) {
                    return 0;
                }
                if (leftMissing) {
                    return 1;
                }
                if (rightMissing) {
                    return -1;
                }
                const compared = String(left.timestamp).localeCompare(String(right.timestamp));
                return sortDir === "asc" ? compared : -compared;
            }

            const compared = String(left?.tag_name ?? "").localeCompare(
                String(right?.tag_name ?? ""),
                undefined,
                { sensitivity: "base" }
            );
            return sortDir === "asc" ? compared : -compared;
        });
    }, [tagList, search, serverFilter, groupFilter, connectionFilter, sortKey, sortDir]);

    const toggleTheme = () => {
        if (typeof onToggleTheme === "function") {
            onToggleTheme();
        }
    };

    const toggleSort = (key) => {
        if (sortKey === key) {
            setSortDir((current) => (current === "asc" ? "desc" : "asc"));
            return;
        }
        setSortKey(key);
        setSortDir(key === "time" ? "desc" : "asc");
    };

    const sortArrow = (key) => {
        if (sortKey !== key) {
            return "";
        }
        return sortDir === "asc" ? " ↑" : " ↓";
    };

    const showInitialLoading = loading && tagList.length === 0;
    const showEmpty = !loading && tagList.length === 0 && !error;
    const showNoMatches = !showInitialLoading && tagList.length > 0 && visibleTags.length === 0;
    const canManagePLC = !access || ["admin", "superadmin"].includes(access.role);

    return (
        <div className="hmi-home" data-theme={theme}>
            <header className="hmi-home__top">
                <div className="hmi-home__intro">
                    <p className="hmi-home__eyebrow">PLC Gateway</p>
                    <h1>PLC Gateway Overview</h1>
                    <p className="hmi-home__subtitle">Industrial Monitoring &amp; Live Data</p>
                </div>

                <div className="hmi-home__meta">
                    <div className="hmi-home__clock" aria-live="off">
                        <span className="hmi-home__meta-label">Current time</span>
                        <strong>{formatClock(now)}</strong>
                    </div>

                    <button
                        type="button"
                        className="hmi-home__theme"
                        onClick={toggleTheme}
                        aria-pressed={theme === "dark"}
                        aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
                    >
                        <ThemeIcon theme={theme} />
                        <span>{theme === "dark" ? "Dark" : "Light"}</span>
                    </button>

                    <div
                        className={`hmi-home__refresh ${error ? "is-error" : ""} ${refreshing ? "is-refreshing" : ""}`}
                        aria-live="polite"
                    >
                        <span className="hmi-home__meta-label">Last successful refresh</span>
                        <strong>
                            {refreshing && lastSuccessAt ? "Refreshing… " : ""}
                            {lastSuccessAt ? formatClock(lastSuccessAt) : "Waiting for data"}
                        </strong>
                        {error ? <span className="hmi-home__refresh-error">{error}</span> : null}
                    </div>
                </div>
            </header>

            {canManagePLC ? <div className="hmi-home__shortcuts">
                <Link to="/add-tag?tab=server">
                    <button type="button" className="hmi-home__shortcut">
                        <PlusIcon />
                        Add Server
                    </button>
                </Link>
                <Link to="/add-tag">
                    <button type="button" className="hmi-home__shortcut">
                        <PlusIcon />
                        PLC Management
                    </button>
                </Link>
            </div> : null}

            <section className="hmi-home__kpis" aria-label="Overview">
                <KpiCard
                    icon={<TagIcon />}
                    label="Total Tags"
                    value={showInitialLoading ? "—" : metrics.total}
                    hint="Returned by the live feed"
                />
                <KpiCard
                    icon={<ServerIcon />}
                    label="OPC Servers"
                    value={showInitialLoading ? "—" : metrics.servers}
                    hint="Distinct servers in this response"
                />
                <KpiCard
                    icon={<CheckIcon />}
                    label="Online"
                    value={showInitialLoading ? "—" : metrics.online}
                    hint="Tags currently connected"
                    tone="good"
                />
                <KpiCard
                    icon={<MissingIcon />}
                    label="Offline / Unknown"
                    value={showInitialLoading ? "—" : metrics.offline}
                    hint="Tags offline or without a known connection"
                    tone="warn"
                />
            </section>

            <section className="hmi-home__panel" aria-label="Live tag monitoring">
                <div className="hmi-home__panel-head">
                    <div>
                        <h2>Live Tag Monitoring</h2>
                        <p>
                            {showInitialLoading
                                ? "Loading live values"
                                : `Showing ${visibleTags.length} of ${tagList.length} tags`}
                        </p>
                    </div>

                    <div className="hmi-home__tools">
                        <label className="hmi-home__field">
                            <span>Search tags</span>
                            <input
                                type="search"
                                value={search}
                                onChange={(event) => setSearch(event.target.value)}
                                placeholder="Search by tag name"
                            />
                        </label>

                        <label className="hmi-home__field">
                            <span>Server</span>
                            <select
                                value={serverFilter}
                                onChange={(event) => setServerFilter(event.target.value)}
                            >
                                <option value="all">All servers</option>
                                {serverOptions.map((name) => (
                                    <option key={name} value={name}>
                                        {name}
                                    </option>
                                ))}
                            </select>
                        </label>

                        <label className="hmi-home__field">
                            <span>Connection</span>
                            <select
                                value={connectionFilter}
                                onChange={(event) => setConnectionFilter(event.target.value)}
                            >
                                <option value="all">All statuses</option>
                                <option value="online">Online</option>
                                <option value="offline">Offline</option>
                                <option value="unknown">Unknown</option>
                            </select>
                        </label>

                        <label className="hmi-home__field">
                            <span>Group</span>
                            <select
                                value={groupFilter}
                                onChange={(event) => setGroupFilter(event.target.value)}
                            >
                                <option value="all">All groups</option>
                                {groupOptions.map((name) => (
                                    <option key={name} value={name}>
                                        {name}
                                    </option>
                                ))}
                            </select>
                        </label>
                    </div>
                </div>

                {showInitialLoading ? (
                    <div className="hmi-home__state">Loading live tag data…</div>
                ) : null}

                {showEmpty ? (
                    <div className="hmi-home__state">No tags found</div>
                ) : null}

                {!showInitialLoading && tagList.length === 0 && error ? (
                    <div className="hmi-home__state hmi-home__state--error">
                        Live tag data could not be loaded.
                    </div>
                ) : null}

                {showNoMatches ? (
                    <div className="hmi-home__state">No tags match the current search or filter.</div>
                ) : null}

                {visibleTags.length > 0 ? (
                    <div className="hmi-home__table-wrap">
                        <table className="hmi-home__table">
                            <thead>
                                <tr>
                                    <th aria-sort={sortKey === "name" ? (sortDir === "asc" ? "ascending" : "descending") : "none"}>
                                        <button type="button" onClick={() => toggleSort("name")}>
                                            Tag Name{sortArrow("name")}
                                        </button>
                                    </th>
                                    <th>OPC Server</th>
                                    <th>Current Value</th>
                                    <th aria-sort={sortKey === "time" ? (sortDir === "asc" ? "ascending" : "descending") : "none"}>
                                        <button type="button" onClick={() => toggleSort("time")}>
                                            Last Update{sortArrow("time")}
                                        </button>
                                    </th>
                                    <th>Connection</th>
                                </tr>
                            </thead>
                            <tbody>
                                {visibleTags.map((tag, index) => {
                                    const connectionStatus = tag.connection_status || "UNKNOWN";
                                    return (
                                        <tr key={`${tag.tag_name ?? "tag"}-${tag.server_name ?? "server"}-${index}`}>
                                            <td className="hmi-home__tag-name">{tag.tag_name}</td>
                                            <td>{tag.server_name}</td>
                                            <td className="hmi-home__value">{formatLiveValue(tag)}</td>
                                            <td className="hmi-home__time">{formatLocalDateTime(tag.timestamp)}</td>
                                            <td>
                                                <span className={`hmi-home__status ${connectionStatus === "ONLINE" ? "is-available" : "is-missing"}`}>
                                                    {connectionStatus}
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                ) : null}
            </section>
        </div>
    );
};

const KpiCard = ({ icon, label, value, hint, tone }) => (
    <article className={`hmi-kpi ${tone ? `hmi-kpi--${tone}` : ""}`}>
        <div className="hmi-kpi__icon" aria-hidden="true">
            {icon}
        </div>
        <div>
            <p className="hmi-kpi__label">{label}</p>
            <p className="hmi-kpi__value">{value}</p>
            <p className="hmi-kpi__hint">{hint}</p>
        </div>
    </article>
);

const ThemeIcon = ({ theme }) => (
    <svg viewBox="0 0 24 24" aria-hidden="true">
        {theme === "dark" ? (
            <path
                fill="currentColor"
                d="M21 14.5A8.5 8.5 0 0 1 9.5 3 7 7 0 1 0 21 14.5Z"
            />
        ) : (
            <path
                fill="currentColor"
                d="M12 4.2a.9.9 0 0 1 .9.9V6a.9.9 0 1 1-1.8 0v-.9a.9.9 0 0 1 .9-.9Zm0 12.8a4.2 4.2 0 1 1 0-8.4 4.2 4.2 0 0 1 0 8.4Zm7.2-5.1a.9.9 0 0 1-.9.9h-.9a.9.9 0 1 1 0-1.8h.9a.9.9 0 0 1 .9.9ZM6.6 12a.9.9 0 0 1-.9.9H4.8a.9.9 0 1 1 0-1.8h.9a.9.9 0 0 1 .9.9Zm9.75-5.35a.9.9 0 0 1 0 1.27l-.64.64a.9.9 0 1 1-1.27-1.27l.64-.64a.9.9 0 0 1 1.27 0ZM9.56 15.44a.9.9 0 0 1 0 1.27l-.64.64a.9.9 0 0 1-1.27-1.27l.64-.64a.9.9 0 0 1 1.27 0Zm6.15 1.27a.9.9 0 0 1-1.27 0l-.64-.64a.9.9 0 0 1 1.27-1.27l.64.64a.9.9 0 0 1 0 1.27ZM8.29 8.56a.9.9 0 0 1-1.27 0l-.64-.64A.9.9 0 0 1 7.65 6.65l.64.64a.9.9 0 0 1 0 1.27Z"
            />
        )}
    </svg>
);

const PlusIcon = () => (
    <svg viewBox="0 0 24 24" aria-hidden="true">
        <path fill="currentColor" d="M11.1 5.2a.9.9 0 0 1 1.8 0v5.9h5.9a.9.9 0 1 1 0 1.8h-5.9v5.9a.9.9 0 1 1-1.8 0v-5.9H5.2a.9.9 0 1 1 0-1.8h5.9V5.2Z" />
    </svg>
);

const TagIcon = () => (
    <svg viewBox="0 0 24 24" aria-hidden="true">
        <path fill="currentColor" d="M3.8 12.6 11.2 5.2A2.2 2.2 0 0 1 12.8 4.5H18a1.5 1.5 0 0 1 1.5 1.5v5.2a2.2 2.2 0 0 1-.7 1.6l-7.4 7.4a1.8 1.8 0 0 1-2.5 0l-5.1-5.1a1.8 1.8 0 0 1 0-2.5Zm11.7-4.1a1.3 1.3 0 1 0 0-2.6 1.3 1.3 0 0 0 0 2.6Z" />
    </svg>
);

const ServerIcon = () => (
    <svg viewBox="0 0 24 24" aria-hidden="true">
        <path fill="currentColor" d="M5 4.5h14A1.5 1.5 0 0 1 20.5 6v3A1.5 1.5 0 0 1 19 10.5H5A1.5 1.5 0 0 1 3.5 9V6A1.5 1.5 0 0 1 5 4.5Zm0 9h14a1.5 1.5 0 0 1 1.5 1.5v3a1.5 1.5 0 0 1-1.5 1.5H5A1.5 1.5 0 0 1 3.5 18v-3A1.5 1.5 0 0 1 5 13.5ZM7 7.2a1 1 0 1 0 0 2 1 1 0 0 0 0-2Zm0 9a1 1 0 1 0 0 2 1 1 0 0 0 0-2Z" />
    </svg>
);

const CheckIcon = () => (
    <svg viewBox="0 0 24 24" aria-hidden="true">
        <path fill="currentColor" d="M12 3.5a8.5 8.5 0 1 1 0 17 8.5 8.5 0 0 1 0-17Zm3.7 6.1-4.4 5.1a1 1 0 0 1-1.5.1L8.2 12.9a1 1 0 1 1 1.3-1.5l1.3 1.2 3.7-4.3a1 1 0 0 1 1.5 1.3Z" />
    </svg>
);

const MissingIcon = () => (
    <svg viewBox="0 0 24 24" aria-hidden="true">
        <path fill="currentColor" d="M12 3.5a8.5 8.5 0 1 1 0 17 8.5 8.5 0 0 1 0-17Zm-3.2 7.6a.9.9 0 0 0 0 1.8h6.4a.9.9 0 1 0 0-1.8H8.8Z" />
    </svg>
);

export default HomePage;
